import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Box, Chip, ListItem, ListItemButton, ListItemText, SxProps, Theme, Typography } from '@mui/material'
import React from 'react'
import Highlight from './Highlight'

interface QuickSearchResultItemProps {
  isSelected: boolean;
  onClick: () => void;
  query: string;

  icon?: React.ReactNode;
  badge?: string;

  subsection?: string;
  text: string;
  description?: string;
}

export default function QuickSearchResultItem(props: QuickSearchResultItemProps): React.ReactElement {
  const {
    icon,
    badge,
    subsection,
    text,
    description,
    query,
    onClick,
    isSelected,
  } = props

  const listItemRef = React.useRef<HTMLLIElement | null>(null)

  React.useEffect(() => {
    if (isSelected) {
      listItemRef.current?.scrollIntoView({ block: 'end', inline: 'nearest', behavior: 'smooth' })
    }
  }, [isSelected])

  return (
    <ListItem
      ref={listItemRef}
      className={isSelected ? 'quick-search-item-selected' : ''}
      sx={styles.listItem(isSelected)}
      disablePadding
    >
      <ListItemButton
        onClick={onClick}
        selected={isSelected}
        sx={styles.listItemButton}
      >
        {icon && <Box sx={styles.listIcon}>{icon}</Box>}

        <ListItemText
          primary={
            <Box sx={styles.listItemWrapper}>
              <Typography
                variant="body2"
                component="span"
                sx={styles.heading}
              >
                {subsection && (
                  <Box component="span" sx={styles.subHeading}>
                    <Highlight text={subsection} query={query} />
                  </Box>
                )}
                {
                  (text || badge) && <ChevronRightIcon sx={styles.chevron} />
                }
                <Box component="span">
                  <Highlight text={text} query={query} />
                </Box>
              </Typography>

              {badge && (
                <Chip label={badge} size="small" color="success" sx={styles.badge} />
              )}
            </Box>
          }
          secondary={
            description ? (
              <Typography variant="body2" color="text.secondary" sx={styles.description}>
                <Highlight text={description} query={query} />
              </Typography>
            ) : null
          }
        />

        <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label="MUI X"
            size="small"
            variant="outlined"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 600,
              display: badge === 'Premium' ? 'flex' : 'none',
            }}
          />
        </Box>
      </ListItemButton>
    </ListItem>
  )
}

const styles = {
  listItem: (isSelected: boolean): SxProps<Theme> => ({
    borderLeft: 3,
    borderColor: isSelected ? 'primary.main' : 'transparent',
    backgroundColor: isSelected ? 'action.selected' : 'transparent',
    '& ul': { padding: 0 },
  }),
  listItemButton: {
    py: 1.5,
    px: 2,
    '&:hover': {
      bgcolor: 'action.hover',
    },
  },
  listIcon: {
    display: 'flex',
    mr: 2,
    color: 'text.secondary',
  },
  badge: {
    height: 20,
    fontSize: '0.7rem',
  },
  chevron: {
    fontSize: 16,
    color: 'text.disabled',
  },
  listItemWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  heading: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  },
  subHeading: {
    fontWeight: 600,
  },
  description: {
    mt: 0.5,
    fontSize: '0.875rem',
  },
}

